import React, { useState, useEffect, useRef } from "react";
import {
  Input,
  LabelWithContainer,
  Label,
  ErrorMessage,
  SuggestionsList,
} from "./FormStyles";
import Spinner from "../Common/Spinner";
import CheckRender from "../Common/CheckRender";
import useEmpresa from "../../hooks/useEmpresa";
import {
  validationSchemaRequired,
  validationSchemaEmail,
  validationSchemaPhone,
  validationSchemaCustom,
} from "../../utils/SchemaValidations";
import { isEmptyValue } from "../../utils/StringUtil";

const CustomInput = ({
  type,
  label,
  placeholder,
  value,
  onChange,
  handleKeyPress,
  fontSize,
  borderRadius,
  responsiveFontSize,
  responsivePadding,
  width,
  height,
  maxLength,
  disabled,
  required,
  isLabelWithContainer,
  labelWidth,
  labelHeight,
  validationConfig,
  showRequired,
  suggestions,
  setSelectedSuggestion,
  dataLoading,
  updatedSuggestions,
  minDate,
  maxDate,
  onBlur,
}) => {
  const empresa = useEmpresa();

  const [errors, setErrors] = useState({});

  const [touched, setTouched] = useState(false);

  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (required && showRequired && isEmptyValue(value)) {
      requireField()
    }
  }, [showRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isThisFocused = document.activeElement === inputRef.current;
    setShowSuggestions(isThisFocused && updatedSuggestions?.length > 0);
    setFilteredSuggestions(updatedSuggestions);
  }, [updatedSuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFocus = () => {
    if (suggestions?.length > 0 && !isEmptyValue(value)) {
      setShowSuggestions(true);
    }
  };

  const requireField = async () => {
    try {
      await validationSchemaRequired.validate({
        requiredField: null,
      });
      setErrors({});
    } catch (error) {
      setErrors({ [error.path]: error.message });
    }
  };

  const validate = async () => {
    try {
      if (onBlur) {
        onBlur();
      }
      
      if (!touched) {
        return;
      }

      if (required) {
        await validationSchemaRequired.validate({
          requiredField: value,
        });
      }

      if (validationConfig) {
        await validationSchemaCustom(
          validationConfig?.validationCallBack,
          validationConfig?.errorMessage
        ).validate({
          customField: value,
        });
      }

      switch (type) {
        case "email":
          await validationSchemaEmail.validate({
            emailField: value,
          });
          break;
        case "phone":
          await validationSchemaPhone.validate({
            phoneField: value,
          });
          break;
        default:
          break;
      }

      setErrors({});
    } catch (error) {
      setErrors({ [error.path]: error.message });
    }
  };

  const handleChange = (event) => {
    const inputValue = event?.target?.value;

    setTouched(true);

    onChange(inputValue);

    if (suggestions?.length > 0) {
      const filtered = suggestions.filter((item) => {
        const lowerCaseInput = inputValue?.toLowerCase();
        const lowerCaseCodigo = item?.codigo?.toLowerCase();
        const lowerCaseDescripcion = item?.descripcion?.toLowerCase();

        return (
          lowerCaseCodigo?.includes(lowerCaseInput) ||
          lowerCaseDescripcion?.includes(lowerCaseInput)
        );
      });
      setFilteredSuggestions(filtered);
      setShowSuggestions(!isEmptyValue(inputValue));
    }

  };

  const handleSelect = (item) => {
    if (setSelectedSuggestion) {
      setSelectedSuggestion(item);
    }
    onChange(item?.descripcion, false);
    setShowSuggestions(false);
  };

  const inputContent = (
    <>
      <Input
        ref={inputRef}
        type={type}
        placeholder={placeholder}
        fontSize={fontSize}
        borderRadius={borderRadius}
        responsiveFontSize={responsiveFontSize}
        responsivePadding={responsivePadding}
        value={value || ""}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        onBlur={validate}
        onFocus={handleFocus}
        font={empresa?.LETRA_PRINCIPAL}
        width={width}
        height={height}
        disabled={disabled}
        maxLength={maxLength}
        minDate={minDate}
        maxDate={maxDate}
      />

      {errors.requiredField && (
        <ErrorMessage>{errors.requiredField}</ErrorMessage>
      )}
      {errors.emailField && <ErrorMessage>{errors.emailField}</ErrorMessage>}
      {errors.phoneField && <ErrorMessage>{errors.phoneField}</ErrorMessage>}
      {errors.customField && <ErrorMessage>{errors.customField}</ErrorMessage>}

      <CheckRender allowed={dataLoading}>
        <Spinner
          size={20}
          colorBorderTop={empresa?.COLOR_PRINCIPAL}
        />
      </CheckRender>

      <CheckRender allowed={!dataLoading}>
        {showSuggestions && (
          <SuggestionsList
            hoverBgColor={empresa?.COLOR_PRINCIPAL}
            style={{
              top: "100%",
              left: 0,
              right: 0,
              border: "1px solid #ccc",
              borderRadius: "4px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              boxSizing: "border-box"
            }}
          >
            {filteredSuggestions.map((item) => (
              <li key={item.codigo+item.descripcion} onClick={() => handleSelect(item)}>
                {`${item.codigo} - ${item.descripcion}`}
              </li>
            ))}
          </SuggestionsList>
        )}
      </CheckRender>
    </>
  );

  return (
    <>
      <CheckRender allowed={label}>
        <CheckRender allowed={isLabelWithContainer}>
          <div>
            <LabelWithContainer
              font={empresa?.LETRA_PRINCIPAL}
              labelWidth={labelWidth}
              labelHeight={labelHeight}
            >
              {label}
            </LabelWithContainer>
          </div>
        </CheckRender>
        <CheckRender allowed={!isLabelWithContainer}>
          <Label font={empresa?.LETRA_PRINCIPAL}>{label}</Label>
        </CheckRender>
      </CheckRender>

      {suggestions !== undefined || updatedSuggestions !== undefined ? (
        <div style={{ position: "relative", width: width || "100%", display: "flex", flexDirection: "column" }}>
          {inputContent}
        </div>
      ) : (
        inputContent
      )}
    </>
  );
};

export default CustomInput;